// src/components/EmployeeLayout.jsx
import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../actions/AuthActions";
import { 
  LayoutDashboard, 
  Calendar, 
  ClipboardList, 
  LogOut, 
  Menu, 
  X, 
  Clock,
  User,
  Bell,
  ChevronDown,
  Truck,
  MapPin
} from "lucide-react";
import LocationIndicator from "./LocationIndicator";
import GPSEnableNotification from "./GPSEnableNotification";
import { toast } from "react-toastify";
import { clearDashboardCache, clearAllCacheOnLogout } from "../utils/clearDashboardCache";

const EmployeeLayout = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { userDetails, loading } = useSelector((state) => state.auth);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Navigation items for Employee
  const navItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/employee/dashboard" },
    { icon: Calendar, label: "Schedule Booking", path: "/employee/schedule-booking" },
    { icon: ClipboardList, label: "My Bookings", path: "/employee/my-bookings" },
     { icon: MapPin, label: "Live Map", path: "/employee/map" },
  ];

  // Update current time
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Close mobile menu on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu with Escape key
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [mobileMenuOpen]);

  // Profile image URL
  const profileImageUrl = userDetails?.ServiceNo
    ? `https://aeroviewbackend.dockyardsoftware.com/User/GetUserImg?Sno=${userDetails.ServiceNo}&t=${Date.now()}`
    : `https://ui-avatars.com/api/?name=${encodeURIComponent(userDetails?.Name || "Employee")}&background=374151&color=fff&size=96&bold=true`;

  const handleLogout = async () => {
    if (window.confirm("Are you sure you want to log out?")) {
      await clearAllCacheOnLogout("employee");
      dispatch(logoutUser());
      navigate("/login");
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
    setShowUserMenu(false);
  };

  const isActive = (path) => location.pathname === path;

  // Format time
  const formattedTime = useMemo(
    () => currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    [currentTime]
  );

  const fullDate = currentTime.toLocaleDateString('en-US', { 
    weekday: 'short', 
    month: 'short', 
    day: 'numeric' 
  });

  const handleClearCache = async () => {
    const cleared = clearDashboardCache("employee");
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
    toast.success(`Cache cleared (${cleared.length} item${cleared.length === 1 ? "" : "s"})`);
    setTimeout(() => window.location.reload(), 350);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ================= Top Header ================= */}
      <header className="fixed top-0 right-0 left-0 z-30 bg-white border-b border-gray-100 shadow-sm safe-area-header">
        <div className="flex items-center justify-between px-3 sm:px-5 py-2.5">
          {/* Left Section */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition"
              aria-label="Open menu"
            >
              <Menu size={18} className="text-gray-600" />
            </button>
            
            {/* Desktop sidebar toggle */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="hidden md:block p-2 hover:bg-gray-100 rounded-lg transition"
              aria-label="Toggle sidebar"
            >
              <Menu size={16} className="text-gray-600" />
            </button>
            
            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gray-900 rounded-lg flex items-center justify-center shadow-sm">
                <Truck size={14} className="text-white" />
              </div>
              <div className="hidden sm:block">
                <span className="text-sm font-semibold text-gray-900">AeroView</span>
                <span className="text-xs text-gray-400 ml-1">Employee</span>
              </div>
            </div>

            {/* Time Display */}
            <div className="hidden md:flex items-center gap-1 text-xs text-gray-500 bg-gray-50 px-2.5 py-1 rounded-full">
              <Clock size={11} />
              {formattedTime}
            </div>
          </div>
          
          {/* Right Section */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Date Display */}
            <div className="hidden lg:block text-xs text-gray-400">
              {fullDate}
            </div>
            
            {/* Location Indicator */}
            <div className="hidden sm:block">
              <LocationIndicator />
            </div>
            
            {/* GPS Notification */}
            <GPSEnableNotification />
            
            {/* Notifications */}
            <button className="relative p-1.5 hover:bg-gray-100 rounded-lg transition">
              <Bell size={16} className="text-gray-600" />
              <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-red-500 rounded-full"></span>
            </button>
            <button
              onClick={handleClearCache}
              className="px-2.5 py-1.5 text-xs text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
            >
              Clear Cache
            </button>
            
            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 pl-2 border-l border-gray-200 hover:bg-gray-50 rounded-lg transition p-1"
                disabled={loading}
              >
                <img
                  src={profileImageUrl}
                  alt={userDetails?.Name || "Employee"}
                  className="w-7 h-7 rounded-full object-cover border border-gray-200"
                />
                <span className="text-xs font-medium text-gray-700 hidden sm:inline">
                  {userDetails?.Name?.split(" ")[0] || "Employee"}
                </span>
                <ChevronDown size={12} className="text-gray-400 hidden sm:block" />
              </button>
              
              {/* Dropdown Menu */}
              {showUserMenu && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowUserMenu(false)}
                  />
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50">
                    <div className="px-3 py-2 border-b border-gray-100">
                      <p className="text-xs font-semibold text-gray-900">{userDetails?.Name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{userDetails?.role || "Employee"}</p>
                      <p className="text-xs text-gray-400 font-mono mt-0.5">{userDetails?.ServiceNo}</p>
                    </div>
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate("/employee/profile");
                      }}
                      className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition flex items-center gap-2"
                    >
                      <User size={12} />
                      Profile Settings
                    </button>
                    <button
                      onClick={handleLogout}
                      disabled={loading}
                      className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition flex items-center gap-2"
                    >
                      <LogOut size={12} />
                      {loading ? "Logging out..." : "Logout"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ================= Desktop Sidebar (Collapsible - Gray Theme) ================= */}
      <aside 
        className={`fixed left-0 top-0 h-full z-20 bg-white border-r border-gray-100 transition-all duration-300 hidden md:block shadow-sm
          ${sidebarOpen ? "w-56" : "w-16"}`}
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        {/* User Info Section */}
        <div className={`flex items-center ${sidebarOpen ? "justify-between px-3" : "justify-center"} py-3 border-b border-gray-100 mt-12`}>
          {sidebarOpen ? (
            <>
              <div className="flex items-center gap-2 min-w-0">
                <img
                  src={profileImageUrl}
                  alt=""
                  className="w-7 h-7 rounded-full object-cover border border-gray-200 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-900 truncate">
                    {userDetails?.Name?.split(" ")[0] || "Employee"}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{userDetails?.role || "Employee"}</p>
                </div>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-1 hover:bg-gray-100 rounded-lg transition flex-shrink-0"
              >
                <ChevronDown size={14} className="text-gray-400 rotate-90" />
              </button>
            </>
          ) : (
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <User size={18} className="text-gray-600" />
            </button>
          )}
        </div>

        {/* Navigation Items */}
        <nav className="flex flex-col h-full p-2">
          <div className="flex-1 space-y-1 mt-3">
            {navItems.map((item, index) => (
              <button
                key={index}
                onClick={() => handleNavigation(item.path)}
                className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg transition-all duration-200 text-sm ${
                  isActive(item.path)
                    ? "bg-gray-900 text-white shadow-sm"
                    : "text-gray-600 hover:bg-gray-50"
                } ${!sidebarOpen && "justify-center"}`}
                title={!sidebarOpen ? item.label : undefined}
              >
                <item.icon size={sidebarOpen ? 16 : 18} />
                {sidebarOpen && <span className="truncate text-xs">{item.label}</span>}
              </button>
            ))}
          </div>
          
          {/* Logout Button */}
          <button
            onClick={handleLogout}
            disabled={loading}
            className={`flex items-center gap-3 px-2 py-2 rounded-lg text-red-600 hover:bg-red-50 transition text-sm mt-2 ${
              !sidebarOpen && "justify-center"
            }`}
            title={!sidebarOpen ? "Logout" : undefined}
          >
            <LogOut size={sidebarOpen ? 16 : 18} />
            {sidebarOpen && <span className="text-xs">{loading ? "..." : "Logout"}</span>}
          </button>
        </nav>
      </aside>

      {/* ================= Mobile Sidebar Drawer ================= */}
      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          <aside className="fixed top-0 left-0 h-full w-64 bg-white shadow-2xl z-50 md:hidden" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gray-900 rounded-lg flex items-center justify-center">
                    <Truck size={14} className="text-white" />
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-gray-900">AeroView</span>
                    <span className="text-xs text-gray-400 ml-1">Employee</span>
                  </div>
                </div>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition"
                >
                  <X size={18} className="text-gray-500" />
                </button>
              </div>
              
              {/* User Info */}
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <img
                    src={profileImageUrl}
                    alt=""
                    className="w-10 h-10 rounded-full object-cover border border-gray-200"
                  />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{userDetails?.Name || "Employee"}</p>
                    <p className="text-xs text-gray-500">{userDetails?.role || "Employee"}</p>
                    <p className="text-xs text-gray-400 font-mono">{userDetails?.ServiceNo}</p>
                  </div>
                </div>
              </div>
              
              {/* Navigation */}
              <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                {navItems.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handleNavigation(item.path)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition text-sm ${
                      isActive(item.path)
                        ? "bg-gray-900 text-white"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <item.icon size={18} />
                    <span>{item.label}</span>
                  </button>
                ))}
              </nav>
              
              {/* Footer */}
              <div className="p-4 border-t border-gray-100 bg-gray-50">
                <div className="flex items-center justify-between mb-3 text-xs text-gray-500">
                  <span>Logged in as</span>
                  <span className="font-mono">{userDetails?.ServiceNo || "Employee"}</span>
                </div>
                <button
                  onClick={handleLogout}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-red-600 hover:bg-red-50 transition text-sm font-medium"
                >
                  <LogOut size={14} />
                  <span>{loading ? "Logging out..." : "Logout"}</span>
                </button>
              </div>
            </div>
          </aside>
        </>
      )}

      {/* ================= Main Content ================= */}
      <main
        className={`transition-all duration-300 ${
          sidebarOpen ? "md:pl-56" : "md:pl-16"
        }`}
        style={{ paddingTop: 'calc(48px + env(safe-area-inset-top))' }}
      >
        <div className="p-3 sm:p-4 md:p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default EmployeeLayout;
